import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app";
import { addPageToPDFData } from "../store/documentSlice";
import {
  convertArrayBufferToImageUrl,
  getImageDimensions,
} from "../../common/utilities/image";
import { calculateScalingFactor } from "../../annotation/utils/scaling";
import { inflate } from "pako";
import { IPdfPageMetadataResponse } from "../interfaces/request";
import pdfPageImageSelector from "../selectors/pdfPageImageSelector";
import {
  removeAnnotatorError,
  setAnnotatorError,
} from "../../annotator/store/AnnotatorSlice";
import { AnnotatorErrorType } from "../../annotator/interfaces/errorType";
import { shallowEqual } from "react-redux";
import http from "../../common/utilities/HttpModule";

const usePDFPage = (page: number, fetchData: boolean) => {
  const dispatch = useAppDispatch();

  const documentId = useAppSelector(
    (state) => state.documentReducer.activeDocument?.id,
    shallowEqual,
  );

  const image = useAppSelector(
    (state) => pdfPageImageSelector(state, page),
    shallowEqual,
  );

  const authenticatedFetch = useCallback(
    async (url: string, signal: AbortSignal): Promise<ArrayBuffer> => {
      const response = await http.get<ArrayBuffer>(url, {
        responseType: "arraybuffer",
        signal,
      });
      return response.data;
    },
    [],
  );

  const loadPdfPageAndData = useCallback(
    async (fetchData: boolean, signal: AbortSignal) => {
      if (!documentId) {
        return;
      }

      if (!fetchData) {
        return;
      }

      try {
        // Check if aborted before starting
        if (signal.aborted) {
          return;
        }

        // Fetch page image with abort signal
        const imageBuffer = await authenticatedFetch(
          `/api/v1/documents/${documentId}/page/${page}/image`,
          signal,
        );

        // Check if aborted after image fetch
        if (signal.aborted) {
          return;
        }

        const image = convertArrayBufferToImageUrl(imageBuffer);
        const { width: imgWidth, height: imgHeight } =
          await getImageDimensions(image);

        // Check if aborted after image processing
        if (signal.aborted) {
          return;
        }

        const [imgWidthScalingFactor, imgHeightScalingFactor] =
          calculateScalingFactor(imgWidth, imgHeight);

        dispatch(
          addPageToPDFData({
            id: "",
            image,
            page,
            dimension: {
              unit: "pixels",
              width: imgWidth / imgWidthScalingFactor,
              height: imgHeight / imgHeightScalingFactor,
            },
            tokens: [],
            text: "",
          }),
        );

        // Check if aborted before starting metadata/text fetch
        if (signal.aborted) {
          return;
        }

        // Fetch metadata and text in parallel with abort signal
        const [pdfMetaDataBuffer, textBuffer] = await Promise.all([
          authenticatedFetch(
            `/api/v1/documents/${documentId}/page/${page}/text-layer-lite`,
            signal,
          ),
          authenticatedFetch(
            `/api/v1/documents/${documentId}/page/${page}/text`,
            signal,
          ),
        ]);

        // Check if aborted after metadata/text fetch
        if (signal.aborted) {
          return;
        }

        const pdfMetaData: IPdfPageMetadataResponse = JSON.parse(
          inflate(pdfMetaDataBuffer, { to: "string" }),
        );

        const { width, height } = pdfMetaData.dimension;
        const [widthScalingFactor, heightScalingFactor] =
          calculateScalingFactor(width, height);
        const pdfText = inflate(textBuffer, { to: "string" });

        dispatch(
          addPageToPDFData({
            id: "",
            image,
            page,
            dimension: {
              unit: pdfMetaData.dimension.unit,
              width: width / widthScalingFactor,
              height: height / heightScalingFactor,
            },
            tokens: pdfMetaData.tokens.map((t, index) => ({
              ...t,
              dataI: index,
              left: t.left / widthScalingFactor,
              width: t.width / widthScalingFactor,
              top: t.top / heightScalingFactor,
              height: t.height / heightScalingFactor,
            })),
            text: pdfText,
          }),
        );
        dispatch(removeAnnotatorError(AnnotatorErrorType.PDF_LOADING_ERROR));
      } catch (e: unknown) {
        // Don't set error if the request was aborted
        if (signal?.aborted) {
          console.log(`Page ${page} fetch aborted`);
          return;
        }

        console.error(`Error loading page ${page}:`, e);
        dispatch(setAnnotatorError(AnnotatorErrorType.PDF_LOADING_ERROR));
      }
    },
    [documentId, page, dispatch, authenticatedFetch],
  );

  useEffect(() => {
    const controller = new AbortController();

    if (fetchData) {
      loadPdfPageAndData(fetchData, controller.signal);
    }

    return () => {
      // Cleanup: abort all in-flight requests
      // This will cause the fetch requests to throw AbortError
      controller.abort();
    };
  }, [loadPdfPageAndData, fetchData]);

  return { image };
};

export default usePDFPage;
